import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import usePagination from "../../../../hooks/usePagination";
import { FilterAutocomplete, Table } from "../../../../components/ui";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../../../context";
import { HELIOS_LIVE_SIM_TIF_COLUMNS } from "../../constants";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendSearchParamsToQuery, TitleUpdated } from "../../utils";
import { SectionTitle } from "../../styledComponents";

const HeliosLiveSIMTraderInformationFormSearch: React.FC = () => {
  const dispatch = useDispatch();
  const pagination = usePagination();
  const {
    loading,
    data: { results, updated },
  } = useSelector((state) => state.complianceSearch.watchlistLiveSimTif);

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } =
    tabsTablesSearchState.fundedAccountQuickSearchTab.heliosLiveSIMTraderInformationFormSearchTable;

  const { isError, error } = useQuery({
    retry: false,
    queryKey: [
      COMPLIANCE_API_URLS.getLiveSimTif,
      Object.values(search),
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      appendSearchParamsToQuery(search, query);

      dispatch(complianceSearchActions.fetchWatchlistLiveSimTifRequest());
      const response = await ComplianceAPI.getWatchlistLiveSimTif(query.toString(), signal);
      pagination.setCount(response.data.results.count);
      dispatch(complianceSearchActions.fetchWatchlistLiveSimTifSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(complianceSearchActions.fetchWatchlistLiveSimTifFailed());
      console.error("Error fetching LiveSIM Trader Information Form", error);
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [search, pagination.limit]);

  return (
    <SectionTitle title="LiveSIM TIF">
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={(values) => {
            setTabsTablesSearchState({
              ...tabsTablesSearchState,
              fundedAccountQuickSearchTab: {
                ...tabsTablesSearchState.fundedAccountQuickSearchTab,
                heliosLiveSIMTraderInformationFormSearchTable: {
                  search: values,
                },
              },
            });
          }}
          filters={{
            Name: "names",
            Email: "email",
            Status: "status",
            "Entries ID": "compliance_entries_id",
          }}
        />
      </div>
      <Table
        title={<TitleUpdated value={updated?.submission_timestamp} />}
        loading={loading}
        pagination={pagination}
        columns={HELIOS_LIVE_SIM_TIF_COLUMNS}
        extractKey={(row) => row.id}
        data={results.rows}
        csv_table_name="Funded account quick search - LiveSIM TIF"
      />
    </SectionTitle>
  );
};

export default HeliosLiveSIMTraderInformationFormSearch;
